import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronRight, GitCompare, History, MessageSquare,
  Lightbulb, MoreHorizontal, Pencil, Plus, Rocket, RotateCcw, StickyNote, Trash2, Users,
} from 'lucide-react';

import WorshipLayout from '@/components/WorshipLayout';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/worship/songwriting`;

const JOURNEY_STAGES = [
  ['idee', 'Idee'], ['repetitie', 'Repetitie'], ['dienst', 'Dienst'], ['gepubliceerd', 'Gepubliceerd'],
];
const JOURNEY_LABELS = Object.fromEntries([...JOURNEY_STAGES, ['gearchiveerd', 'Gearchiveerd']]);
const SECTION_TYPES = [
  ['couplet', 'Couplet'], ['refrein', 'Refrein'], ['bridge', 'Bridge'], ['intro', 'Intro'],
  ['outro', 'Outro'], ['pre_refrein', 'Pre-refrein'], ['tag', 'Tag'], ['overig', 'Overig'],
];
const COLLABORATOR_ROLES = [
  ['co_writer', 'Co-writer'], ['composer', 'Componist'], ['arranger', 'Arrangeur'],
  ['worship_leader', 'Worship leader'], ['theological_reviewer', 'Theologisch reviewer'],
];
const ROLE_LABELS = {
  owner: 'Eigenaar', co_writer: 'Co-writer', composer: 'Componist', arranger: 'Arrangeur',
  worship_leader: 'Worship leader', theological_reviewer: 'Theologisch reviewer',
};
const VERSION_KIND_LABELS = { initial: 'Eerste versie', autosave: 'Automatisch opgeslagen', checkpoint: 'Revisiemoment', restore: 'Herstelde versie' };
const EMPTY_META_FORM = {
  title: '', subtitle: '', artist: '', composer: '', copyright: '', default_key: '', bpm: '', time_signature: '', capo: '', language: '',
  themes: '', bible_passages: [{ reference: '', explanation: '' }], inspiration_note: '',
};

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
      return `${field ? `${field}: ` : ''}${item?.msg || 'Ongeldige waarde'}`;
    }).join(' · ');
  }
  return fallback;
};

const formatDate = (value) => value ? new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '';

const SECTION_LABELS = Object.fromEntries(SECTION_TYPES);

const sectionLabel = (section) => section.label || SECTION_LABELS[section.section_type] || 'Onderdeel';

const WritingSection = ({ section, index, total, canEditContent, canManageStructure, onSave, onDelete, onMove, onComment }) => {
  const [local, setLocal] = useState(section);
  const dirtyRef = useRef(false);
  const [saveState, setSaveState] = useState('');

  useEffect(() => { setLocal(section); dirtyRef.current = false; }, [section.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dirtyRef.current) return undefined;
    const timer = window.setTimeout(() => {
      onSave(section.id, {
        section_type: local.section_type, label: local.label || null,
        position: local.position, content: local.content || '', notes: local.notes || null,
      });
      dirtyRef.current = false;
      setSaveState('Opgeslagen');
    }, 800);
    return () => window.clearTimeout(timer);
  }, [local, onSave, section.id]);

  const change = (field, value) => {
    setLocal((current) => ({ ...current, [field]: value }));
    dirtyRef.current = true;
    setSaveState('Wijziging wordt opgeslagen…');
  };

  return (
    <article className="songwriting-writing-section">
      <div className="flex items-center gap-3">
        <span className="songwriting-section-number">{index + 1}</span>
        {canManageStructure ? (
          <Select value={local.section_type} onValueChange={(value) => change('section_type', value)}>
            <SelectTrigger className="h-8 w-32 border-0 bg-transparent px-1 text-xs font-bold uppercase tracking-[0.14em] text-teal-700"><SelectValue /></SelectTrigger>
            <SelectContent>{SECTION_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        ) : <span className="text-xs font-bold uppercase tracking-[0.14em] text-teal-700">{sectionLabel(local)}</span>}
        <Input
          value={local.label || ''} onChange={(event) => change('label', event.target.value)}
          placeholder={`${SECTION_LABELS[local.section_type] || 'Onderdeel'} ${index + 1}`}
          className="h-8 min-w-0 flex-1 border-0 bg-transparent px-1 text-sm text-slate-600 shadow-none focus-visible:ring-0"
          disabled={!canManageStructure}
          aria-label={`Naam van ${sectionLabel(local)}`}
        />
        <details className="relative shrink-0">
          <summary className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" aria-label="Sectieacties"><MoreHorizontal className="h-4 w-4" /></summary>
          <div className="absolute right-0 top-9 z-10 grid min-w-44 gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
            <button type="button" disabled={!canManageStructure || index === 0} onClick={() => onMove(index, -1)} className="rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Omhoog verplaatsen</button>
            <button type="button" disabled={!canManageStructure || index === total - 1} onClick={() => onMove(index, 1)} className="rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Omlaag verplaatsen</button>
            <button type="button" onClick={() => onComment(section.id)} className="rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">Reactie plaatsen</button>
            {canManageStructure && <button type="button" onClick={() => onDelete(section.id)} className="rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">Sectie verwijderen</button>}
          </div>
        </details>
      </div>
      <p className="mt-1 pl-11 text-xs text-slate-400">{saveState || (canEditContent ? 'Schrijf hier de tekst van dit onderdeel' : 'Alleen lezen')}</p>
      {canEditContent ? (
        <Textarea
          value={local.content || ''}
          onChange={(event) => change('content', event.target.value)}
          placeholder={`Schrijf ${sectionLabel(local).toLowerCase()} hier...`}
          className="songwriting-lyrics-input mt-3 min-h-36 resize-y border-0 bg-transparent px-11 py-2 text-base leading-8 text-slate-800 shadow-none placeholder:text-slate-300 focus-visible:ring-0 sm:text-lg"
          aria-label={`Tekst van ${sectionLabel(local)}`}
        />
      ) : <p className="mt-3 whitespace-pre-wrap px-11 text-base leading-8 text-slate-700">{local.content || 'Nog geen tekst toegevoegd.'}</p>}
    </article>
  );
};

const cleanPreviewText = (value) => (value || '')
  .replace(/^\{[^}]+\}\s*$/gm, '')
  .replace(/\[([^\]]+)\]/g, '')
  .trim();

const WritingPreview = ({ project, sections }) => (
  <div className="songwriting-preview rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Voorbeeld</p>
    <h2 className="mt-3 text-xl font-semibold text-slate-900">{project.title}</h2>
    {project.subtitle && <p className="mt-1 text-sm italic text-slate-500">{project.subtitle}</p>}
    <div className="mt-6 space-y-6">
      {sections.map((section) => <section key={section.id}><h3 className="text-xs font-bold uppercase tracking-[0.14em] text-teal-700">{sectionLabel(section)}</h3><p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-slate-700">{cleanPreviewText(section.content) || 'Nog geen tekst.'}</p></section>)}
      {!sections.length && <p className="text-sm leading-7 text-slate-500">Je voorbeeld verschijnt hier zodra je een sectie toevoegt.</p>}
    </div>
  </div>
);

const SongwritingStudioPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { hasCapability, hasModule } = useAuth();
  const available = hasCapability('songwriting_access') && hasModule('worship');

  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schrijven');
  const [previewOpen, setPreviewOpen] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');

  const [editMetaDialogOpen, setEditMetaDialogOpen] = useState(false);
  const [editMetaForm, setEditMetaForm] = useState(EMPTY_META_FORM);

  const [collaboratorDialogOpen, setCollaboratorDialogOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [invite, setInvite] = useState({ user_id: '', role: 'co_writer' });

  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentSectionId, setCommentSectionId] = useState(null);

  const [reviews, setReviews] = useState([]);
  const [reviewNote, setReviewNote] = useState('');

  const [versions, setVersions] = useState([]);
  const [compareIds, setCompareIds] = useState({ left: '', right: '' });
  const [comparison, setComparison] = useState(null);

  const [publication, setPublication] = useState(null);

  const loadProjects = useCallback(async () => {
    if (!available) { setLoading(false); return; }
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Kon songprojecten niet laden'));
    } finally {
      setLoading(false);
    }
  }, [available]);

  const openProject = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Songproject kon niet worden geopend'));
      navigate('/worship/songwriting');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!available) { setLoading(false); return; }
    if (projectId) openProject(projectId);
    else { setProject(null); loadProjects(); }
  }, [available, projectId, openProject, loadProjects]);

  const loadComments = useCallback(async (id) => {
    if (!id) { setComments([]); return; }
    try {
      const response = await axios.get(`${API}/projects/${id}/comments`);
      setComments(response.data || []);
    } catch (error) { toast.error('Reacties konden niet worden geladen'); }
  }, []);

  const loadReviews = useCallback(async (id) => {
    if (!id) { setReviews([]); return; }
    try {
      const response = await axios.get(`${API}/projects/${id}/reviews`);
      setReviews(response.data || []);
    } catch (error) { toast.error('Reviews konden niet worden geladen'); }
  }, []);

  const loadVersions = useCallback(async (id) => {
    if (!id) { setVersions([]); return; }
    try {
      const response = await axios.get(`${API}/projects/${id}/versions`);
      const items = response.data || [];
      setVersions(items);
      setCompareIds((current) => ({ left: current.left || items[1]?.id || items[0]?.id || '', right: current.right || items[0]?.id || '' }));
    } catch (error) { toast.error('Versiegeschiedenis kon niet worden geladen'); }
  }, []);

  const loadPublication = useCallback(async (id) => {
    if (!id) { setPublication(null); return; }
    try {
      const response = await axios.get(`${API}/projects/${id}/publication`);
      setPublication(response.data);
    } catch (error) { toast.error(apiErrorMessage(error, 'Publicatiestatus kon niet worden geladen')); }
  }, []);

  useEffect(() => { loadComments(project?.id); }, [loadComments, project?.id]);
  useEffect(() => { loadReviews(project?.id); }, [loadReviews, project?.id]);
  useEffect(() => { setComparison(null); loadVersions(project?.id); }, [loadVersions, project?.id]);
  useEffect(() => { loadPublication(project?.id); }, [loadPublication, project?.id]);
  // Preflight/publication data depends on sections and the journey stage,
  // both of which change via local setProject() calls that don't touch
  // project.id -- so re-fetch every time the Publish tab is opened rather
  // than relying on a stale snapshot from whenever the project first loaded.
  useEffect(() => { if (activeTab === 'publiceren') loadPublication(project?.id); }, [activeTab, loadPublication, project?.id]);

  const projectPayload = (overrides = {}) => ({
    title: project.title,
    subtitle: project.subtitle || null,
    artist: project.artist || null,
    composer: project.composer || null,
    copyright: project.copyright || null,
    default_key: project.default_key || null,
    alternative_keys: project.alternative_keys || [],
    bpm: project.bpm ?? null,
    time_signature: project.time_signature || null,
    capo: project.capo ?? null,
    language: project.language || null,
    themes: project.themes || [],
    bible_passages: (project.bible_passages || []).map(({ reference, explanation }) => ({ reference, explanation: explanation || null })),
    inspiration_note: project.inspiration_note || null,
    journey_stage: project.journey_stage,
    ...overrides,
  });

  const createProject = async (event) => {
    event.preventDefault();
    if (!createTitle.trim()) return;
    try {
      const response = await axios.post(`${API}/projects`, { title: createTitle, journey_stage: 'idee' });
      setCreateDialogOpen(false);
      setCreateTitle('');
      toast.success('Songproject aangemaakt');
      navigate(`/worship/songwriting/${response.data.id}`);
    } catch (error) { toast.error(apiErrorMessage(error, 'Songproject kon niet worden aangemaakt')); }
  };

  const deleteProject = async (item) => {
    if (!window.confirm(`Weet je zeker dat je "${item.title}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await axios.delete(`${API}/projects/${item.id}`);
      toast.success('Songproject verwijderd');
      await loadProjects();
    } catch (error) { toast.error(apiErrorMessage(error, 'Verwijderen mislukt')); }
  };

  const advanceJourney = async (stage) => {
    try {
      const response = await axios.put(`${API}/projects/${project.id}`, projectPayload({ journey_stage: stage }));
      setProject((current) => ({ ...current, ...response.data }));
      toast.success('Song Journey bijgewerkt');
      if (activeTab === 'publiceren') await loadPublication(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Status kon niet worden gewijzigd')); }
  };

  const openEditMeta = () => {
    setEditMetaForm({
      title: project.title || '', subtitle: project.subtitle || '', default_key: project.default_key || '',
      artist: project.artist || '', composer: project.composer || '', copyright: project.copyright || '',
      bpm: project.bpm ?? '', time_signature: project.time_signature || '', capo: project.capo ?? '',
      language: project.language || '', themes: (project.themes || []).join(', '),
      bible_passages: (project.bible_passages || []).length ? project.bible_passages.map((p) => ({ ...p })) : [{ reference: '', explanation: '' }],
      inspiration_note: project.inspiration_note || '',
    });
    setEditMetaDialogOpen(true);
  };

  const saveMetadata = async (event) => {
    event.preventDefault();
    const payload = projectPayload({
      title: editMetaForm.title,
      subtitle: editMetaForm.subtitle || null,
      artist: editMetaForm.artist || null,
      composer: editMetaForm.composer || null,
      copyright: editMetaForm.copyright || null,
      default_key: editMetaForm.default_key || null,
      bpm: editMetaForm.bpm === '' ? null : Number(editMetaForm.bpm),
      time_signature: editMetaForm.time_signature || null,
      capo: editMetaForm.capo === '' ? null : Number(editMetaForm.capo),
      language: editMetaForm.language || null,
      themes: editMetaForm.themes.split(',').map((t) => t.trim()).filter(Boolean),
      bible_passages: editMetaForm.bible_passages
        .filter((p) => p.reference.trim())
        .map((p) => ({ reference: p.reference.trim(), explanation: p.explanation?.trim() || null })),
      inspiration_note: editMetaForm.inspiration_note || null,
    });
    try {
      const response = await axios.put(`${API}/projects/${project.id}`, payload);
      setProject((current) => ({ ...current, ...response.data }));
      setEditMetaDialogOpen(false);
      toast.success('Songgegevens opgeslagen');
    } catch (error) { toast.error(apiErrorMessage(error, 'Opslaan mislukt')); }
  };

  const addBiblePassageRow = () => setEditMetaForm((f) => ({ ...f, bible_passages: [...f.bible_passages, { reference: '', explanation: '' }] }));
  const removeBiblePassageRow = (index) => setEditMetaForm((f) => ({ ...f, bible_passages: f.bible_passages.filter((_, i) => i !== index) }));
  const updateBiblePassageRow = (index, field, value) => setEditMetaForm((f) => ({
    ...f, bible_passages: f.bible_passages.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
  }));

  const addSection = async () => {
    try {
      const response = await axios.post(`${API}/projects/${project.id}/sections`, {
        section_type: 'couplet', position: project.sections?.length || 0, content: '',
      });
      setProject((current) => ({ ...current, sections: [...(current.sections || []), response.data] }));
    } catch (error) { toast.error(apiErrorMessage(error, 'Sectie kon niet worden toegevoegd')); }
  };

  const saveSection = useCallback(async (sectionId, payload) => {
    try {
      const response = await axios.put(`${API}/projects/${project.id}/sections/${sectionId}`, payload);
      setProject((current) => ({ ...current, sections: (current.sections || []).map((s) => (s.id === sectionId ? response.data : s)) }));
    } catch (error) { toast.error(apiErrorMessage(error, 'Sectie kon niet worden opgeslagen')); }
  }, [project?.id]);

  const deleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/sections/${sectionId}`);
      setProject((current) => ({ ...current, sections: (current.sections || []).filter((s) => s.id !== sectionId) }));
    } catch (error) { toast.error(apiErrorMessage(error, 'Sectie kon niet worden verwijderd')); }
  };

  const moveSection = async (index, direction) => {
    const sections = project.sections || [];
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const items = reordered.map((section, i) => ({ id: section.id, position: i }));
    try {
      const response = await axios.put(`${API}/projects/${project.id}/sections/reorder`, { items });
      setProject((current) => ({ ...current, sections: response.data }));
    } catch (error) { toast.error(apiErrorMessage(error, 'Herordenen mislukt')); }
  };

  const commentOnSection = (sectionId) => {
    setCommentSectionId(sectionId);
            setActiveTab('feedback');
  };

  const openCollaborators = async () => {
    try {
      const requests = [axios.get(`${API}/projects/${project.id}/collaborators`)];
      if (project.permissions?.can_manage_collaborators) requests.push(axios.get(`${API}/projects/${project.id}/candidates`));
      const responses = await Promise.all(requests);
      setCollaborators(responses[0].data || []);
      setCandidates(responses[1]?.data || []);
      setCollaboratorDialogOpen(true);
    } catch (error) { toast.error(apiErrorMessage(error, 'Deelnemers konden niet worden geladen')); }
  };

  const addCollaborator = async () => {
    if (!invite.user_id) return;
    try {
      await axios.post(`${API}/projects/${project.id}/collaborators`, invite);
      toast.success('Deelnemer uitgenodigd');
      setInvite({ user_id: '', role: 'co_writer' });
      await openCollaborators();
    } catch (error) { toast.error(apiErrorMessage(error, 'Uitnodigen mislukt')); }
  };

  const removeCollaborator = async (userId) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/collaborators/${userId}`);
      await openCollaborators();
    } catch (error) { toast.error(apiErrorMessage(error, 'Verwijderen mislukt')); }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/projects/${project.id}/comments`, { content: commentText, section_id: commentSectionId });
      setCommentText('');
      await loadComments(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Reactie kon niet worden geplaatst')); }
  };

  const setCommentStatus = async (commentId, status) => {
    try {
      await axios.put(`${API}/projects/${project.id}/comments/${commentId}/status`, { status });
      await loadComments(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Bijwerken mislukt')); }
  };

  const submitReview = async (status) => {
    try {
      await axios.put(`${API}/projects/${project.id}/review`, { status, note: reviewNote });
      setReviewNote('');
      toast.success('Review opgeslagen');
      await loadReviews(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Review kon niet worden opgeslagen')); }
  };

  const createCheckpoint = async () => {
    const label = window.prompt('Naam voor dit revisiemoment (bv. "Klaar voor repetitie"):');
    if (!label || !label.trim()) return;
    try {
      await axios.post(`${API}/projects/${project.id}/checkpoints`, { label: label.trim() });
      toast.success('Revisiemoment vastgelegd');
      await loadVersions(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Revisiemoment kon niet worden vastgelegd')); }
  };

  const compareVersions = async () => {
    if (!compareIds.left || !compareIds.right) return;
    try {
      const response = await axios.post(`${API}/projects/${project.id}/versions/compare`, {
        left_version_id: compareIds.left, right_version_id: compareIds.right,
      });
      setComparison(response.data);
    } catch (error) { toast.error(apiErrorMessage(error, 'Vergelijken mislukt')); }
  };

  const restoreVersion = async (versionId) => {
    if (!window.confirm('Deze versie herstellen? De huidige songtekst wordt vervangen (blijft terug te vinden in de geschiedenis).')) return;
    try {
      await axios.post(`${API}/projects/${project.id}/versions/${versionId}/restore`);
      toast.success('Versie hersteld');
      await openProject(project.id);
      await loadVersions(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Herstellen mislukt')); }
  };

  const publishProject = async () => {
    try {
      await axios.post(`${API}/projects/${project.id}/publish`);
      toast.success('Lied gepubliceerd naar de songbibliotheek');
      await openProject(project.id);
      await loadPublication(project.id);
    } catch (error) { toast.error(apiErrorMessage(error, 'Publiceren mislukt')); }
  };

  if (!available) {
    return (
      <WorshipLayout>
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-500">
          Je hebt geen toegang tot de Songwriting Studio.
        </CardContent></Card>
      </WorshipLayout>
    );
  }

  const sections = project?.sections || [];

  return (
    <WorshipLayout>
      {!project ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Songwriting Studio</h1>
              <p className="text-sm text-slate-500">Schrijf, ontwikkel en beoordeel nieuwe liederen samen met je team.</p>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Nieuw songproject</Button>
          </div>
          {loading ? (
            <div className="h-40 animate-pulse rounded-2xl bg-slate-200" />
          ) : projects.length === 0 ? (
            <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-500">
              Nog geen songprojecten. Start een nieuw lied om te beginnen.
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {projects.map((item) => (
                <Card key={item.id} className="border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary">{JOURNEY_LABELS[item.journey_stage] || item.journey_stage}</Badge>
                          <Badge variant="outline">{ROLE_LABELS[item.access_role] || item.access_role}</Badge>
                        </div>
                        <h2 className="mt-3 text-lg font-semibold text-slate-900">{item.title}</h2>
                        {item.subtitle && <p className="text-sm italic text-slate-500">{item.subtitle}</p>}
                      </div>
                      {item.permissions?.can_manage_collaborators && (
                        <Button variant="ghost" size="icon" onClick={() => deleteProject(item)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                      {item.default_key && <span>Toonsoort {item.default_key}</span>}
                      {item.bpm && <span>{item.bpm} bpm</span>}
                      <span>{item.section_count} onderdelen</span>
                    </div>
                    <Button variant="outline" onClick={() => navigate(`/worship/songwriting/${item.id}`)} className="mt-4 w-full rounded-xl">
                      Open songproject <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => navigate('/worship/songwriting')} className="rounded-xl"><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900">{project.title}</h1>
                  <Badge variant="secondary">{JOURNEY_LABELS[project.journey_stage] || project.journey_stage}</Badge>
                  {project.permissions?.can_manage_structure && (
                    <Button variant="ghost" size="icon" onClick={openEditMeta} className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
                {project.subtitle && <p className="text-sm italic text-slate-500">{project.subtitle}</p>}
                <p className="text-sm text-slate-500">
                  {[
                    project.artist && `Artiest: ${project.artist}`,
                    project.composer && `Auteur: ${project.composer}`,
                    project.default_key && `Toonsoort ${project.default_key}`,
                    project.bpm && `${project.bpm} bpm`,
                    project.time_signature,
                    ROLE_LABELS[project.access_role],
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={openCollaborators}><Users className="mr-2 h-4 w-4" /> Samenwerken</Button>
          </div>

          <Card className="border-0 bg-slate-50/70 shadow-none"><CardContent className="flex flex-wrap items-center gap-2 p-3">
            {JOURNEY_STAGES.map(([value, label], index) => {
              const currentIndex = JOURNEY_STAGES.findIndex(([v]) => v === project.journey_stage);
              const reached = index <= currentIndex;
              const clickable = project.permissions?.can_advance_journey && project.journey_stage !== value;
              return (
                <React.Fragment key={value}>
                  {index > 0 && <div className={`h-px w-6 ${reached ? 'bg-teal-500' : 'bg-slate-200'}`} />}
                  <button
                    type="button" disabled={!clickable} onClick={() => advanceJourney(value)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      project.journey_stage === value ? 'bg-teal-600 text-white'
                        : reached ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'
                    } ${clickable ? 'hover:opacity-80' : 'cursor-default'}`}
                  >
                    {label}
                  </button>
                </React.Fragment>
              );
            })}
            {project.journey_stage !== 'gearchiveerd' && project.permissions?.can_advance_journey && (
              <Button type="button" variant="ghost" size="sm" onClick={() => advanceJourney('gearchiveerd')} className="ml-2 text-slate-500">Archiveren</Button>
            )}
          </CardContent></Card>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start overflow-x-auto bg-slate-100/80 sm:w-auto">
              <TabsTrigger value="schrijven">Schrijven</TabsTrigger>
              <TabsTrigger value="feedback">Feedback</TabsTrigger>
              <TabsTrigger value="versies">Versies</TabsTrigger>
              <TabsTrigger value="publiceren">Publiceren</TabsTrigger>
            </TabsList>

            <TabsContent value="schrijven" className="songwriting-workspace mt-4">
              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.38fr)]">
                <main className="min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-8 sm:py-7">
                  <div className="mb-8 max-w-3xl">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Schrijven</p>
                    <h2 className="mt-2 text-xl font-semibold text-slate-900">Geef je lied ruimte</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-500">Werk rustig per onderdeel. Je wijzigingen worden automatisch opgeslagen.</p>
                  </div>
                  <div className="max-w-3xl space-y-2">
                    {sections.map((section, index) => (
                      <WritingSection
                        key={section.id} section={section} index={index} total={sections.length}
                        canEditContent={!!project.permissions?.can_edit_content}
                        canManageStructure={!!project.permissions?.can_manage_structure}
                        onSave={saveSection} onDelete={deleteSection} onMove={moveSection} onComment={commentOnSection}
                      />
                    ))}
                    {project.permissions?.can_manage_structure && <Button variant="outline" onClick={addSection} className="mt-5 rounded-xl"><Plus className="mr-2 h-4 w-4" /> Sectie toevoegen</Button>}
                    {!sections.length && <p className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">Begin met een sectie zoals een couplet of refrein.</p>}
                  </div>
                </main>
                <aside className="space-y-4 xl:self-start">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Songgegevens</p>
                    <div className="mt-4 space-y-3 text-sm">
                      <div><p className="text-xs text-slate-400">Thema</p><p className="mt-1 font-medium text-slate-800">{project.themes?.join(' · ') || 'Nog geen thema toegevoegd'}</p></div>
                      <div><p className="text-xs text-slate-400">Werktoonsoort</p><p className="mt-1 font-medium text-slate-800">{project.default_key || 'Nog niet gekozen'}{project.bpm ? ` · ${project.bpm} bpm` : ''}</p></div>
                      <div><p className="text-xs text-slate-400">Inspiratie</p><p className="mt-1 whitespace-pre-wrap leading-6 text-slate-600">{project.inspiration_note || 'Bewaar hier waarom je dit lied schrijft.'}</p></div>
                    </div>
                    {project.permissions?.can_manage_structure && <Button variant="outline" size="sm" onClick={openEditMeta} className="mt-4 w-full rounded-xl"><Pencil className="mr-2 h-3.5 w-3.5" /> Songgegevens bewerken</Button>}
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Creatieve context</p><Lightbulb className="h-4 w-4 text-amber-500" /></div>
                    <div className="mt-4 space-y-3 text-sm text-slate-600"><p className="flex gap-2"><StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{project.bible_passages?.length ? project.bible_passages.map((item) => item.reference).join(' · ') : 'Voeg later inspiratie of Bijbelverwijzingen toe.'}</p><p className="flex gap-2"><Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />Werk samen met schrijvers via Samenwerken.</p></div>
                  </div>
                  <Button type="button" variant="outline" className="w-full rounded-xl" onClick={() => setPreviewOpen(true)}>
                    Voorbeeld bekijken <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </aside>
              </div>
              <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                  <DialogHeader><DialogTitle>Voorbeeld van {project.title}</DialogTitle></DialogHeader>
                  <WritingPreview project={project} sections={sections} />
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="feedback" className="space-y-6">
              <Card className="border-0 shadow-sm"><CardContent className="space-y-3 p-5">
                <h3 className="font-semibold text-slate-900">Reacties</h3>
                {commentSectionId && (
                  <div className="flex items-center gap-2 text-xs text-teal-700">
                    Reageren op sectie {sections.find((s) => s.id === commentSectionId)?.label || sections.find((s) => s.id === commentSectionId)?.section_type}
                    <button type="button" onClick={() => setCommentSectionId(null)} className="underline">annuleren</button>
                  </div>
                )}
                <div className="space-y-2">
                  {comments.map((comment) => (
                    <div key={comment.id} className="rounded-xl border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">
                          {comment.author_name}
                          {comment.section_id && <span className="ml-2 text-xs font-normal text-slate-400">op een sectie</span>}
                        </p>
                        {project.permissions?.can_resolve_comments && (
                          <Button variant="ghost" size="sm" onClick={() => setCommentStatus(comment.id, comment.status === 'resolved' ? 'open' : 'resolved')}>
                            {comment.status === 'resolved' ? 'Heropenen' : 'Afhandelen'}
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{comment.content}</p>
                      <p className="mt-1 text-xs text-slate-400">{formatDate(comment.created_at)} · {comment.status === 'resolved' ? 'Afgehandeld' : 'Open'}</p>
                    </div>
                  ))}
                  {!comments.length && <p className="text-sm text-slate-500">Nog geen reacties.</p>}
                </div>
                {project.permissions?.can_comment && (
                  <div className="space-y-2 border-t pt-3">
                    <Textarea value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder="Schrijf een reactie…" />
                    <Button onClick={addComment} disabled={!commentText.trim()}>Plaatsen</Button>
                  </div>
                )}
              </CardContent></Card>

              <Card className="border-0 shadow-sm"><CardContent className="space-y-3 p-5">
                <h3 className="font-semibold text-slate-900">Theologische review</h3>
                <div className="space-y-2">
                  {reviews.map((review) => (
                    <div key={review.id} className="rounded-xl border p-3">
                      <p className="text-sm font-medium text-slate-900">
                        {review.reviewer_name} ·{' '}
                        <span className={review.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}>
                          {review.status === 'approved' ? 'Goedgekeurd' : 'Wijzigingen gevraagd'}
                        </span>
                      </p>
                      {review.note && <p className="text-sm text-slate-600">{review.note}</p>}
                      <p className="mt-1 text-xs text-slate-400">{formatDate(review.updated_at)}</p>
                    </div>
                  ))}
                  {!reviews.length && <p className="text-sm text-slate-500">Nog geen reviewbesluiten.</p>}
                </div>
                {project.access_role === 'theological_reviewer' && (
                  <div className="space-y-2 border-t pt-3">
                    <Textarea value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} placeholder="Toelichting bij je reviewbesluit (optioneel)…" />
                    <div className="flex gap-2">
                      <Button onClick={() => submitReview('approved')} className="bg-emerald-600 hover:bg-emerald-700">Goedkeuren</Button>
                      <Button onClick={() => submitReview('changes_requested')} variant="outline">Wijzigingen vragen</Button>
                    </div>
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="versies" className="space-y-4">
              <Card className="border-0 shadow-sm"><CardContent className="space-y-3 p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Versiegeschiedenis</h3>
                  {project.permissions?.can_edit_content && (
                    <Button variant="outline" size="sm" onClick={createCheckpoint}><History className="mr-2 h-4 w-4" /> Revisiemoment vastleggen</Button>
                  )}
                </div>
                <div className="space-y-2">
                  {versions.map((version) => (
                    <div key={version.id} className="flex items-center justify-between rounded-xl border p-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          v{version.version_number} · {VERSION_KIND_LABELS[version.kind] || version.kind}
                          {version.label && ` — ${version.label}`}
                        </p>
                        <p className="text-xs text-slate-400">{version.created_by_name} · {formatDate(version.created_at)}</p>
                      </div>
                      {project.permissions?.can_manage_structure && (
                        <Button variant="ghost" size="sm" onClick={() => restoreVersion(version.id)}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Herstellen</Button>
                      )}
                    </div>
                  ))}
                  {!versions.length && <p className="text-sm text-slate-500">Nog geen versiegeschiedenis.</p>}
                </div>
                {versions.length > 1 && (
                  <div className="space-y-3 border-t pt-4">
                    <Label>Twee versies vergelijken</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={compareIds.left} onValueChange={(value) => setCompareIds((c) => ({ ...c, left: value }))}>
                        <SelectTrigger className="w-48"><SelectValue placeholder="Versie A" /></SelectTrigger>
                        <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>v{v.version_number}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={compareIds.right} onValueChange={(value) => setCompareIds((c) => ({ ...c, right: value }))}>
                        <SelectTrigger className="w-48"><SelectValue placeholder="Versie B" /></SelectTrigger>
                        <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>v{v.version_number}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="outline" onClick={compareVersions}><GitCompare className="mr-2 h-4 w-4" /> Vergelijk</Button>
                    </div>
                    {comparison && (
                      <div className="space-y-2">
                        {comparison.sections.filter((item) => item.changed).map((item) => (
                          <pre key={item.section_id} className="overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs">{item.diff}</pre>
                        ))}
                        {!comparison.sections.some((item) => item.changed) && <p className="text-sm text-slate-500">Geen songtekstverschillen tussen deze versies.</p>}
                      </div>
                    )}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="publiceren" className="space-y-4">
              <Card className="border-0 shadow-sm"><CardContent className="space-y-4 p-5">
                <h3 className="font-semibold text-slate-900">Publiceren naar de songbibliotheek</h3>
                {publication?.preflight?.issues?.length ? (
                  <ul className="space-y-1 text-sm text-amber-700">
                    {publication.preflight.issues.map((issue, index) => <li key={index}>• {issue.message}</li>)}
                  </ul>
                ) : (
                  <p className="text-sm text-emerald-600">Klaar om te publiceren.</p>
                )}
                {publication?.linked_worship_song && (
                  <p className="text-sm text-slate-600">
                    Al gekoppeld aan <span className="font-medium">{publication.linked_worship_song.title}</span> in de liedbibliotheek — opnieuw publiceren werkt dit lied bij.
                  </p>
                )}
                {publication?.can_publish ? (
                  <Button onClick={publishProject} disabled={!publication?.preflight?.can_publish} className="rounded-xl">
                    <Rocket className="mr-2 h-4 w-4" /> {publication?.linked_worship_song ? 'Opnieuw publiceren' : 'Publiceren'}
                  </Button>
                ) : (
                  <p className="text-sm text-slate-500">Alleen de eigenaar met worshipbeheer-rechten kan publiceren.</p>
                )}
                {!!publication?.publications?.length && (
                  <div className="space-y-1 border-t pt-3">
                    <p className="text-sm font-medium text-slate-600">Publicatiegeschiedenis</p>
                    {publication.publications.map((pub) => (
                      <p key={pub.id} className="text-xs text-slate-400">{formatDate(pub.published_at)} · door {pub.published_by_name}</p>
                    ))}
                  </div>
                )}
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nieuw songproject</DialogTitle></DialogHeader>
          <form onSubmit={createProject} className="space-y-4">
            <div className="space-y-2">
              <Label>Titel</Label>
              <Input value={createTitle} onChange={(event) => setCreateTitle(event.target.value)} placeholder="Titel van het lied" autoFocus />
            </div>
            <DialogFooter><Button type="submit" disabled={!createTitle.trim()}>Aanmaken</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editMetaDialogOpen} onOpenChange={setEditMetaDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Songgegevens bewerken</DialogTitle></DialogHeader>
          <form onSubmit={saveMetadata} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Titel</Label>
                <Input value={editMetaForm.title} onChange={(event) => setEditMetaForm((f) => ({ ...f, title: event.target.value }))} required />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Ondertitel</Label>
                <Input value={editMetaForm.subtitle} onChange={(event) => setEditMetaForm((f) => ({ ...f, subtitle: event.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Artiest / uitvoerder</Label>
                <Input value={editMetaForm.artist} onChange={(event) => setEditMetaForm((f) => ({ ...f, artist: event.target.value }))} placeholder="Wie voert dit lied uit?" />
              </div>
              <div className="space-y-2">
                <Label>Componist</Label>
                <Input value={editMetaForm.composer} onChange={(event) => setEditMetaForm((f) => ({ ...f, composer: event.target.value }))} placeholder="Naam van de componist" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Auteursrecht / copyright</Label>
                <Input value={editMetaForm.copyright} onChange={(event) => setEditMetaForm((f) => ({ ...f, copyright: event.target.value }))} placeholder="Bijvoorbeeld © 2026 Gemeente Zoea" />
              </div>
              <div className="space-y-2">
                <Label>Toonsoort</Label>
                <Input value={editMetaForm.default_key} onChange={(event) => setEditMetaForm((f) => ({ ...f, default_key: event.target.value }))} placeholder="bv. D" />
              </div>
              <div className="space-y-2">
                <Label>Tempo (bpm)</Label>
                <Input type="number" min="20" max="300" value={editMetaForm.bpm} onChange={(event) => setEditMetaForm((f) => ({ ...f, bpm: event.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Maatsoort</Label>
                <Input value={editMetaForm.time_signature} onChange={(event) => setEditMetaForm((f) => ({ ...f, time_signature: event.target.value }))} placeholder="bv. 4/4" />
              </div>
              <div className="space-y-2">
                <Label>Capo</Label>
                <Input type="number" min="0" max="24" value={editMetaForm.capo} onChange={(event) => setEditMetaForm((f) => ({ ...f, capo: event.target.value }))} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Taal</Label>
                <Input value={editMetaForm.language} onChange={(event) => setEditMetaForm((f) => ({ ...f, language: event.target.value }))} placeholder="bv. Nederlands" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Thema's (komma-gescheiden)</Label>
                <Input value={editMetaForm.themes} onChange={(event) => setEditMetaForm((f) => ({ ...f, themes: event.target.value }))} placeholder="bv. genade, aanbidding, hoop" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Inspiratie / achtergrond</Label>
                <Textarea value={editMetaForm.inspiration_note} onChange={(event) => setEditMetaForm((f) => ({ ...f, inspiration_note: event.target.value }))} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Gekoppelde Bijbelteksten</Label>
                {editMetaForm.bible_passages.map((passage, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Input
                      value={passage.reference} onChange={(event) => updateBiblePassageRow(index, 'reference', event.target.value)}
                      placeholder="bv. Psalm 23:1-4" className="w-40"
                    />
                    <Input
                      value={passage.explanation || ''} onChange={(event) => updateBiblePassageRow(index, 'explanation', event.target.value)}
                      placeholder="Toelichting (optioneel)" className="flex-1"
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeBiblePassageRow(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addBiblePassageRow}><Plus className="mr-2 h-4 w-4" /> Bijbeltekst toevoegen</Button>
              </div>
            </div>
            <DialogFooter><Button type="submit">Opslaan</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={collaboratorDialogOpen} onOpenChange={setCollaboratorDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Samenwerken aan {project?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {collaborators.map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-xl border p-3">
                <div>
                  <p className="font-semibold">{item.user?.name || item.user?.email}</p>
                  <p className="text-sm text-slate-500">{ROLE_LABELS[item.role]}</p>
                </div>
                {project?.permissions?.can_manage_collaborators && (
                  <Button variant="ghost" size="icon" onClick={() => removeCollaborator(item.user_id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                )}
              </div>
            ))}
            {!collaborators.length && <p className="text-sm text-slate-500">Er zijn nog geen deelnemers.</p>}
            {project?.permissions?.can_manage_collaborators && (
              <div className="space-y-3 border-t pt-4">
                <Label>Deelnemer uitnodigen</Label>
                <Select value={invite.user_id} onValueChange={(user_id) => setInvite({ ...invite, user_id })}>
                  <SelectTrigger><SelectValue placeholder="Kies een persoon" /></SelectTrigger>
                  <SelectContent>{candidates.map((candidate) => <SelectItem key={candidate.id} value={candidate.id}>{candidate.name || candidate.email}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={invite.role} onValueChange={(role) => setInvite({ ...invite, role })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COLLABORATOR_ROLES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={addCollaborator} disabled={!invite.user_id}>Uitnodigen</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </WorshipLayout>
  );
};

export default SongwritingStudioPage;
